import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { LogoDark } from "../components/Logo";
import { OTPInput } from "../components/OTPInput";
import { api } from "../lib/api";
import { ArrowLeft, CheckCircle2, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

const STEPS = ["username", "otp", "verified", "newpw", "success"];

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [step, setStep] = useState("username");
  const [username, setUsername] = useState("");
  const [otp, setOtp] = useState("");
  const [maskedMobile, setMaskedMobile] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [show, setShow] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const sendOtp = async (e) => {
    e?.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/forgot-password/send-otp", { username });
      setMaskedMobile(data.sent_to_mobile);
      toast.success("OTP sent (use 123456 for demo)");
      setStep("otp");
    } catch {
      setError("Username not found.");
    } finally {
      setLoading(false);
    }
  };

  const verifyOtp = async (e) => {
    e?.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/forgot-password/verify-otp", { username, otp });
      setStep("verified");
      setTimeout(() => setStep("newpw"), 1200);
    } catch {
      setError("Invalid OTP. Try 123456.");
    } finally {
      setLoading(false);
    }
  };

  const resetPw = async (e) => {
    e?.preventDefault();
    setError("");
    if (newPw.length < 6) return setError("Password must be at least 6 characters.");
    if (newPw !== confirmPw) return setError("Passwords do not match.");
    setLoading(true);
    try {
      await api.post("/auth/forgot-password/reset", { username, otp, new_password: newPw });
      setStep("success");
    } catch {
      setError("Reset failed. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const Card = ({ children }) => (
    <div className="w-full max-w-md animate-fade-up rounded-3xl bg-white p-8 shadow-xl shadow-slate-900/5 sm:p-10" data-testid="forgot-card">
      <div className="flex justify-center pb-6"><LogoDark size={44} /></div>
      {children}
    </div>
  );

  return (
    <div className="relative min-h-screen bg-gradient-to-br from-violet-50 via-slate-50 to-indigo-50">
      <div className="pointer-events-none absolute -top-32 -left-32 h-96 w-96 rounded-full bg-violet-200/40 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-32 -right-32 h-96 w-96 rounded-full bg-indigo-200/40 blur-3xl" />

      <div className="relative flex min-h-screen items-center justify-center px-4 py-12">
        {step === "username" && (
          <Card>
            <h1 className="text-center text-xl font-bold text-slate-900">Forgot Password</h1>
            <p className="mt-1 text-center text-sm text-slate-500">Enter your username to receive an OTP.</p>
            <form onSubmit={sendOtp} className="mt-6 space-y-4">
              <input
                data-testid="forgot-username"
                placeholder="Username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                required
              />
              {error && <p className="text-sm text-rose-600">{error}</p>}
              <button data-testid="forgot-send-otp" disabled={loading} className="w-full rounded-xl bg-indigo-600 py-3 font-semibold text-white shadow hover:bg-indigo-700 disabled:opacity-60">
                {loading ? "Sending..." : "Send OTP"}
              </button>
              <Link to="/login" className="flex items-center justify-center gap-1 text-sm text-slate-500 hover:text-slate-700">
                <ArrowLeft size={14} /> Back to login
              </Link>
            </form>
          </Card>
        )}

        {step === "otp" && (
          <Card>
            <h1 className="text-center text-xl font-bold text-slate-900">Enter OTP</h1>
            <p className="mt-1 text-center text-sm text-slate-500">
              We've sent a 6-digit OTP to {maskedMobile || "your mobile"}.
            </p>
            <form onSubmit={verifyOtp} className="mt-6 space-y-5">
              <OTPInput value={otp} onChange={setOtp} length={6} />
              {error && <p className="text-center text-sm text-rose-600">{error}</p>}
              <button data-testid="forgot-verify-otp" disabled={loading || otp.length !== 6} className="w-full rounded-xl bg-indigo-600 py-3 font-semibold text-white shadow hover:bg-indigo-700 disabled:opacity-60">
                {loading ? "Verifying..." : "Verify OTP"}
              </button>
              <button type="button" onClick={sendOtp} className="block w-full text-center text-sm text-indigo-600 hover:underline">
                Resend OTP
              </button>
            </form>
          </Card>
        )}

        {step === "verified" && (
          <Card>
            <div className="flex flex-col items-center py-6">
              <div className="rounded-full bg-emerald-100 p-4 text-emerald-600"><CheckCircle2 size={40} /></div>
              <h1 className="mt-4 text-xl font-bold text-emerald-700">OTP Verified!</h1>
              <p className="mt-1 text-sm text-slate-500">Taking you to set a new password...</p>
            </div>
          </Card>
        )}

        {step === "newpw" && (
          <Card>
            <h1 className="text-center text-xl font-bold text-slate-900">Set New Password</h1>
            <p className="mt-1 text-center text-sm text-slate-500">Choose a strong password you'll remember.</p>
            <form onSubmit={resetPw} className="mt-6 space-y-4">
              <div className="relative">
                <input
                  data-testid="forgot-new-password"
                  type={show ? "text" : "password"}
                  placeholder="New password"
                  value={newPw}
                  onChange={(e) => setNewPw(e.target.value)}
                  className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 pr-12 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                />
                <button type="button" onClick={() => setShow((v) => !v)} className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400">
                  {show ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              <input
                data-testid="forgot-confirm-password"
                type={show ? "text" : "password"}
                placeholder="Confirm new password"
                value={confirmPw}
                onChange={(e) => setConfirmPw(e.target.value)}
                className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
              />
              {error && <p className="text-sm text-rose-600">{error}</p>}
              <button data-testid="forgot-reset-submit" disabled={loading} className="w-full rounded-xl bg-indigo-600 py-3 font-semibold text-white shadow hover:bg-indigo-700 disabled:opacity-60">
                {loading ? "Resetting..." : "Reset Password"}
              </button>
            </form>
          </Card>
        )}

        {step === "success" && (
          <Card>
            <div className="flex flex-col items-center py-6">
              <div className="rounded-full bg-emerald-100 p-5 text-emerald-600"><CheckCircle2 size={48} /></div>
              <h1 className="mt-5 text-2xl font-bold text-emerald-700">Password Reset Successful!</h1>
              <p className="mt-2 text-center text-sm text-slate-500">Your password has been updated. You can now sign in with your new password.</p>
              <button onClick={() => navigate("/login")} data-testid="forgot-back-login" className="mt-6 w-full rounded-xl bg-indigo-600 py-3 font-semibold text-white shadow hover:bg-indigo-700">
                Back to Login
              </button>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
